import React from 'react';
import { NavigationContainer, useNavigation } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { View, StyleSheet, Platform, Dimensions } from 'react-native';
import { ThemeProvider } from './src/contexts/ThemeContext';
import CalculatorLayout from './src/components/CalculatorLayout';
import ToolsLayout from './src/components/ToolsLayout';
import DashboardLayout from './src/components/DashboardLayout';
import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import AuthModal from './src/components/AuthModal';
import DashboardScreen from './src/screens/DashboardScreen';

// Import screens
import HomeScreen from './src/screens/HomeScreen';
import IndexScreen from './src/screens/IndexScreen';
import IncomeTaxCalculatorScreen from './src/screens/IncomeTaxCalculatorScreen';
import TDSCalculatorScreen from './src/screens/TDSCalculatorScreen.js';
import GSTCalculatorScreen from './src/screens/GSTCalculatorScreen';
import EMICalculatorScreen from './src/screens/EMICalculatorScreen';
import HomeLoanEMICalculatorScreen from './src/screens/HomeLoanEMICalculatorScreen';
import CarLoanEMICalculatorScreen from './src/screens/CarLoanEMICalculatorScreen';
import PersonalLoanEMICalculatorScreen from './src/screens/PersonalLoanEMICalculatorScreen';
import BusinessLoanEMICalculatorScreen from './src/screens/BusinessLoanEMICalculatorScreen';
import DownPaymentCalculatorScreen from './src/screens/DownPaymentCalculatorScreen';
import SWPCalculatorScreen from './src/screens/SWPCalculatorScreen';
import MutualFundReturnsCalculatorScreen from './src/screens/MutualFundReturnsCalculatorScreen';
import FDCalculatorScreen from './src/screens/FDCalculatorScreen';
import RDCalculatorScreen from './src/screens/RDCalculatorScreen';
import LumpsumCalculatorScreen from './src/screens/LumpsumCalculatorScreen';
import HRACalculatorScreen from './src/screens/HRACalculatorScreen';
import CapitalGainsCalculatorScreen from './src/screens/CapitalGainsCalculatorScreen';
import AdvanceTaxCalculatorScreen from './src/screens/AdvanceTaxCalculatorScreen';

import SimpleInterestCalculatorScreen from './src/screens/SimpleInterestCalculatorScreen';
import SIPCalculatorScreen from './src/screens/SIPCalculatorScreen';
import SalaryCalculatorScreen from './src/screens/SalaryCalculatorScreen';
import PFCalculatorScreen from './src/screens/PFCalculatorScreen';
import PPFCalculatorScreen from './src/screens/PPFCalculatorScreen';
import GratuityCalculatorScreen from './src/screens/GratuityCalculatorScreen';
import SSYCalculatorScreen from './src/screens/SSYCalculatorScreen';
import NPSCalculatorScreen from './src/screens/NPSCalculatorScreen';
import RetirementCalculatorScreen from './src/screens/RetirementCalculatorScreen';
import CompoundInterestCalculatorScreen from './src/screens/CompoundInterestCalculatorScreen';
import NSCCalculatorScreen from './src/screens/NSCCalculatorScreen';
import StepUpSIPCalculatorScreen from './src/screens/StepUpSIPCalculatorScreen';
import APYCalculatorScreen from './src/screens/APYCalculatorScreen';
import CAGRCalculatorScreen from './src/screens/CAGRCalculatorScreen';
import FlatVsReducingRateCalculatorScreen from './src/screens/FlatVsReducingRateCalculatorScreen';
import BrokerageCalculatorScreen from './src/screens/BrokerageCalculatorScreen';
import MarginCalculatorScreen from './src/screens/MarginCalculatorScreen';
import InflationCalculatorScreen from './src/screens/InflationCalculatorScreen';
import PostOfficeMISCalculatorScreen from './src/screens/PostOfficeMISCalculatorScreen';
import SCSSCalculatorScreen from './src/screens/SCSSCalculatorScreen';
import StockAverageCalculatorScreen from './src/screens/StockAverageCalculatorScreen';
import XIRRCalculatorScreen from './src/screens/XIRRCalculatorScreen';
import InvoiceGeneratorScreen from './src/screens/InvoiceGeneratorScreen';
import QuotationMakerScreen from './src/screens/QuotationMakerScreen';
import ToolsHomeScreen from './src/screens/ToolsHomeScreen';
import MyProfileScreen from './src/screens/MyProfileScreen';

// Support Pages
import HelpCenterScreen from './src/screens/HelpCenterScreen';
import ContactScreen from './src/screens/ContactScreen';
import FAQScreen from './src/screens/FAQScreen';
import FeedbackScreen from './src/screens/FeedbackScreen';
import ReportIssueScreen from './src/screens/ReportIssueScreen';

// Service Pages
import GSTFilingScreen from './src/screens/GSTFilingScreen';
import ITRFilingScreen from './src/screens/ITRFilingScreen';
import BusinessRegistrationScreen from './src/screens/BusinessRegistrationScreen';
import RTOServicesScreen from './src/screens/RTOServicesScreen';

// Legal Pages
import PrivacyPolicyScreen from './src/screens/PrivacyPolicyScreen';
import TermsOfServiceScreen from './src/screens/TermsOfServiceScreen';
import DisclaimerScreen from './src/screens/DisclaimerScreen';

const Stack = createStackNavigator();

// Custom linking configuration for web URLs
const linking = {
  prefixes: ['http://localhost:19006', 'http://localhost:19007', 'http://localhost:8082', 'https://modernmunshiji.com'],
  config: {
    screens: {
      Home: '',
      CalculatorsHome: 'calculators',
      IncomeTaxCalculator: 'calculators/income-tax-calculator',
      TDSCalculator: 'calculators/tds-calculator',
      GSTCalculator: 'calculators/gst-calculator',
      EMICalculator: 'calculators/emi-calculator',
      MyProfile: 'account/profile',
      HomeLoanEMICalculator: 'calculators/home-loan-emi-calculator',
      CarLoanEMICalculator: 'calculators/car-loan-emi-calculator',
      PersonalLoanEMICalculator: 'calculators/personal-loan-emi-calculator',
      BusinessLoanEMICalculator: 'calculators/business-loan-emi-calculator',
      DownPaymentCalculator: 'calculators/down-payment-calculator',
      SWPCalculator: 'calculators/swp-calculator',
      MutualFundReturnsCalculator: 'calculators/mutual-fund-calculator',
      FDCalculator: 'calculators/fd-calculator',
      RDCalculator: 'calculators/rd-calculator',
      LumpsumCalculator: 'calculators/lumpsum-calculator',
      HRACalculator: 'calculators/hra-calculator',
      CapitalGainsCalculator: 'calculators/capital-gains-calculator',
      AdvanceTaxCalculator: 'calculators/advance-tax-calculator',
      SimpleInterestCalculator: 'calculators/simple-interest-calculator',
      SIPCalculator: 'calculators/sip-calculator',
      SalaryCalculator: 'calculators/salary-calculator',
      PFCalculator: 'calculators/epf-calculator',
      PPFCalculator: 'calculators/ppf-calculator',
      GratuityCalculator: 'calculators/gratuity-calculator',
      SSYCalculator: 'calculators/ssy-calculator',
      NPSCalculator: 'calculators/nps-calculator',
      RetirementCalculator: 'calculators/retirement-calculator',
      CompoundInterestCalculator: 'calculators/compound-interest-calculator',
      NSCCalculator: 'calculators/nsc-calculator',
      StepUpSIPCalculator: 'calculators/step-up-sip-calculator',
      APYCalculator: 'calculators/apy-calculator',
      CAGRCalculator: 'calculators/cagr-calculator',
      FlatVsReducingRateCalculator: 'calculators/flat-vs-reducing-rate-calculator',
      BrokerageCalculator: 'calculators/brokerage-calculator',
      MarginCalculator: 'calculators/margin-calculator',
      InflationCalculator: 'calculators/inflation-calculator',
      PostOfficeMISCalculator: 'calculators/post-office-mis-calculator',
      SCSSCalculator: 'calculators/scss-calculator',
      StockAverageCalculator: 'calculators/stock-average-calculator',
      XIRRCalculator: 'calculators/xirr-calculator',
      ToolsHome: 'tools',
      InvoiceGenerator: 'tools/free-online-invoice-generator',
      QuotationMaker: 'tools/quotation-maker',
      Dashboard: 'tools/dashboard',
      HelpCenter: 'support/help-center',
      Contact: 'support/contact',
      FAQ: 'support/faq',
      Feedback: 'support/feedback',
      ReportIssue: 'support/report-issue',
      GSTFiling: 'services/gst-filing',
      ITRFiling: 'services/itr-filing',
      BusinessRegistration: 'services/business-registration',
      RTOServices: 'services/rto-services',
      PrivacyPolicy: 'privacy-policy',
      TermsOfService: 'terms-of-service',
      Disclaimer: 'disclaimer',
    },
  },
};

const AppContent = () => {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);

  // Get screen dimensions for responsive behavior
  const screenWidth = Dimensions.get('window').width;
  const isMobile = screenWidth < 768;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Protected wrapper for Dashboard: if not signed in, redirect to Home and open Auth modal
  const ProtectedDashboardRoute = (props) => {
    const { session, openAuthModal } = useAuth();
    const navigation = useNavigation();
    React.useEffect(() => {
      if (!session?.user) {
        try { openAuthModal(); } catch (_) { }
        // Redirect away from dashboard when unauthenticated
        try { navigation.replace('Home'); } catch (_) { }
      }
    }, [session?.user]);

    if (!session?.user) {
      // Render nothing while redirecting to prevent dashboard content flashing
      return <View style={{ flex: 1 }} />;
    }

    return (
      <DashboardLayout
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={toggleSidebar}
        onCloseSidebar={closeSidebar}
      >
        <DashboardScreen {...props} />
      </DashboardLayout>
    );
  };

  return (
    <>
      <AuthModal />
      <NavigationContainer linking={Platform.OS === 'web' ? linking : undefined}>
        <Stack.Navigator
          initialRouteName="Home"
          screenOptions={({ route }) => ({
            headerShown: false,
            // Custom layout based on route
            cardStyle: { backgroundColor: '#f8fafc' },
          })}
        >
          <Stack.Screen
            name="Home"
            component={IndexScreen}
            options={{
              cardStyle: { backgroundColor: '#f8fafc' },
            }}
          />

          {/* Calculators landing (previous HomeScreen) */}
          <Stack.Screen
            name="CalculatorsHome"
            component={HomeScreen}
            options={{
              cardStyle: { backgroundColor: '#f8fafc' },
            }}
          />

          {/* Calculator Screens with Sidebar Layout */}
          <Stack.Screen name="IncomeTaxCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <IncomeTaxCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="TDSCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <TDSCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="GSTCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <GSTCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="EMICalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <EMICalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="HomeLoanEMICalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <HomeLoanEMICalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="CarLoanEMICalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <CarLoanEMICalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="PersonalLoanEMICalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <PersonalLoanEMICalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="BusinessLoanEMICalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <BusinessLoanEMICalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="DownPaymentCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <DownPaymentCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="SWPCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <SWPCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="MutualFundReturnsCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <MutualFundReturnsCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="FDCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <FDCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="RDCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <RDCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="LumpsumCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <LumpsumCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="HRACalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <HRACalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="CapitalGainsCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <CapitalGainsCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="AdvanceTaxCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <AdvanceTaxCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="SimpleInterestCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <SimpleInterestCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="SIPCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <SIPCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="SalaryCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <SalaryCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="PFCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <PFCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="PPFCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <PPFCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="GratuityCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <GratuityCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="APYCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <APYCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="CAGRCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <CAGRCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="FlatVsReducingRateCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <FlatVsReducingRateCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="BrokerageCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <BrokerageCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="SSYCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <SSYCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="NPSCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <NPSCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="RetirementCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <RetirementCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="CompoundInterestCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <CompoundInterestCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="NSCCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <NSCCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="StepUpSIPCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <StepUpSIPCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="MarginCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <MarginCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="InflationCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <InflationCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="PostOfficeMISCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <PostOfficeMISCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="SCSSCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <SCSSCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="StockAverageCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <StockAverageCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          <Stack.Screen name="XIRRCalculator">
            {(props) => (
              <CalculatorLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <XIRRCalculatorScreen {...props} />
              </CalculatorLayout>
            )}
          </Stack.Screen>

          {/* Tools: Advanced Invoice Generator */}
          {/* Tools Home */}
          <Stack.Screen name="ToolsHome">
            {(props) => (
              <ToolsLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                hideSidebar={true}
              >
                <ToolsHomeScreen {...props} />
              </ToolsLayout>
            )}
          </Stack.Screen>

          {/* Tools - Invoice Generator */}
          <Stack.Screen name="InvoiceGenerator">
            {(props) => (
              <ToolsLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <InvoiceGeneratorScreen {...props} />
              </ToolsLayout>
            )}
          </Stack.Screen>

          {/* Tools - Quotation Maker */}
          <Stack.Screen name="QuotationMaker">
            {(props) => (
              <ToolsLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <QuotationMakerScreen {...props} />
              </ToolsLayout>
            )}
          </Stack.Screen>

          {/* My Profile - use Dashboard layout so the left sidebar shows */}
          <Stack.Screen name="MyProfile">
            {(props) => (
              <DashboardLayout
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
              >
                <MyProfileScreen {...props} />
              </DashboardLayout>
            )}
          </Stack.Screen>

          {/* Dashboard (Protected) */}
          <Stack.Screen name="Dashboard">
            {(props) => (
              <ProtectedDashboardRoute {...props} />
            )}
          </Stack.Screen>

          {/* Support Pages */}
          <Stack.Screen
            name="HelpCenter"
            component={HelpCenterScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="Contact"
            component={ContactScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="FAQ"
            component={FAQScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="Feedback"
            component={FeedbackScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="ReportIssue"
            component={ReportIssueScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />

          {/* Service Pages */}
          <Stack.Screen
            name="GSTFiling"
            component={GSTFilingScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="ITRFiling"
            component={ITRFilingScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="BusinessRegistration"
            component={BusinessRegistrationScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="RTOServices"
            component={RTOServicesScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />

          {/* Legal Pages */}
          <Stack.Screen
            name="PrivacyPolicy"
            component={PrivacyPolicyScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="TermsOfService"
            component={TermsOfServiceScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
          <Stack.Screen
            name="Disclaimer"
            component={DisclaimerScreen}
            options={{ cardStyle: { backgroundColor: '#f8fafc' } }}
          />
        </Stack.Navigator>
      </NavigationContainer>
    </>
  );
};

export default function App() {
  React.useEffect(() => {
    if (Platform.OS === 'web') {
      // Google Analytics
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-LCGKQBQ6MF';
      document.head.appendChild(script);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-LCGKQBQ6MF');
      `;
      document.head.appendChild(script2);
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'row',
  },
  content: {
    flex: 1,
  },
});